import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Users, Check, X, Trash2, KeyRound, ShieldCheck, Sliders } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  pending: { label: "Wartend", cls: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  active: { label: "Aktiv", cls: "bg-green-100 text-green-800 border-green-300" },
  disabled: { label: "Deaktiviert", cls: "bg-zinc-100 text-zinc-700 border-zinc-300" },
};

const PERM_LEVELS = [
  { value: "none", label: "Kein Zugriff", cls: "bg-zinc-100 text-zinc-700 border-zinc-300" },
  { value: "read", label: "Nur Lesen", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  { value: "write", label: "Schreiben", cls: "bg-green-100 text-green-800 border-green-300" },
];

export default function UsersAdmin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pwForm, setPwForm] = useState({ open: false, uid: null, pw: "" });
  const [modules, setModules] = useState([]);
  const [permForm, setPermForm] = useState({ open: false, user: null, perms: {} });

  const load = async () => {
    setLoading(true);
    try {
      const [u, m] = await Promise.all([
        api.get("/users"),
        api.get("/permissions/modules"),
      ]);
      setUsers(u.data);
      setModules(m.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openPerms = (u) => {
    const current = u.permissions || {};
    const perms = {};
    modules.forEach((m) => {
      perms[m.key] = current[m.key] || "none";
    });
    setPermForm({ open: true, user: u, perms });
  };

  const savePerms = async () => {
    try {
      await api.put(`/users/${permForm.user.id}`, { permissions: permForm.perms });
      toast.success("Berechtigungen aktualisiert.");
      setPermForm({ open: false, user: null, perms: {} });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const update = async (uid, payload) => {
    try {
      await api.put(`/users/${uid}`, payload);
      toast.success("Aktualisiert.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (uid) => {
    try {
      await api.delete(`/users/${uid}`);
      toast.success("Benutzer gelöscht.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const changePw = async () => {
    if (!pwForm.pw || pwForm.pw.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben.");
      return;
    }
    try {
      await api.put(`/users/${pwForm.uid}`, { new_password: pwForm.pw });
      toast.success("Passwort geändert.");
      setPwForm({ open: false, uid: null, pw: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (me?.role !== "admin") {
    return (
      <div className="max-w-md mx-auto bg-white border border-zinc-200 rounded-sm p-8 text-center">
        <ShieldCheck className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
        <h2 className="font-display text-xl uppercase mb-1">Kein Zugriff</h2>
        <p className="text-sm text-zinc-600">
          Diese Seite ist nur für Admins.
        </p>
      </div>
    );
  }

  const pending = users.filter((u) => u.status === "pending");

  return (
    <div className="max-w-[1400px] mx-auto space-y-6" data-testid="users-page">
      <div>
        <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-2">
          ADMINISTRATION · {users.length} BENUTZER
        </div>
        <h1 className="font-display text-4xl uppercase flex items-center gap-3">
          <Users className="w-8 h-8" /> Benutzerverwaltung
        </h1>
      </div>

      {pending.length > 0 && (
        <div
          className="bg-yellow-50 border border-yellow-300 rounded-sm p-4"
          data-testid="pending-banner"
        >
          <div className="mono text-[10px] tracking-[0.25em] text-yellow-800 mb-2">
            FREIGABEN AUSSTEHEND
          </div>
          <div className="space-y-2">
            {pending.map((u) => (
              <div
                key={u.id}
                className="bg-white border border-zinc-200 rounded-sm p-3 flex flex-wrap items-center justify-between gap-2"
                data-testid={`pending-${u.id}`}
              >
                <div>
                  <div className="font-medium">{u.name}</div>
                  <div className="mono text-xs text-zinc-500">{u.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => update(u.id, { status: "active" })}
                    data-testid={`approve-${u.id}`}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-sm h-9"
                  >
                    <Check className="w-4 h-4 mr-1" /> Freigeben
                  </Button>
                  <Button
                    onClick={() => update(u.id, { status: "disabled" })}
                    variant="outline"
                    className="rounded-sm border-zinc-400 h-9"
                    data-testid={`reject-${u.id}`}
                  >
                    <X className="w-4 h-4 mr-1" /> Ablehnen
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-zinc-100">
              <tr>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">BENUTZER</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">ROLLE</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">STATUS</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">ANGELEGT</th>
                <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em]">AKTIONEN</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-8 mono text-xs text-zinc-500">LADEN…</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-zinc-500">Keine Benutzer.</td></tr>
              ) : (
                users.map((u) => {
                  const isMe = u.id === me?.id;
                  const st = STATUS[u.status] || STATUS.active;
                  return (
                    <tr key={u.id} className="border-t border-zinc-100" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">
                          {u.name} {isMe && <span className="text-[10px] mono text-[#F97316] ml-1">DU</span>}
                        </div>
                        <div className="mono text-[11px] text-zinc-500">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Select
                          value={u.role}
                          onValueChange={(v) => update(u.id, { role: v })}
                          disabled={isMe}
                        >
                          <SelectTrigger className="rounded-sm border-zinc-400 h-9 w-32" data-testid={`role-${u.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-sm">
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <Select
                          value={u.status || "active"}
                          onValueChange={(v) => update(u.id, { status: v })}
                          disabled={isMe}
                        >
                          <SelectTrigger
                            className={`rounded-sm h-9 w-36 border ${st.cls}`}
                            data-testid={`status-${u.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-sm">
                            <SelectItem value="pending">Wartend</SelectItem>
                            <SelectItem value="active">Aktiv</SelectItem>
                            <SelectItem value="disabled">Deaktiviert</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 mono text-xs text-zinc-500">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString("de-DE") : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {u.role !== "admin" && (
                            <button
                              onClick={() => openPerms(u)}
                              className="p-2 hover:bg-zinc-100 rounded-sm"
                              title="Berechtigungen"
                              data-testid={`perms-${u.id}`}
                            >
                              <Sliders className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => setPwForm({ open: true, uid: u.id, pw: "" })}
                            className="p-2 hover:bg-zinc-100 rounded-sm"
                            title="Passwort ändern"
                            data-testid={`pw-${u.id}`}
                          >
                            <KeyRound className="w-4 h-4" />
                          </button>
                          {!isMe && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button
                                  className="p-2 hover:bg-red-100 hover:text-red-600 rounded-sm"
                                  title="Löschen"
                                  data-testid={`delete-user-${u.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="rounded-sm">
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    „{u.name}" ({u.email}) wird unwiderruflich entfernt.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="rounded-sm">Abbrechen</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => remove(u.id)}
                                    className="bg-red-600 hover:bg-red-700 rounded-sm"
                                  >
                                    Löschen
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={pwForm.open} onOpenChange={(o) => !o && setPwForm({ open: false, uid: null, pw: "" })}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Passwort ändern</AlertDialogTitle>
            <AlertDialogDescription>
              Neues Passwort für den ausgewählten Benutzer (mindestens 6 Zeichen).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            type="password"
            value={pwForm.pw}
            onChange={(e) => setPwForm((s) => ({ ...s, pw: e.target.value }))}
            placeholder="Neues Passwort"
            data-testid="pw-input"
            className="rounded-sm border-zinc-400"
          />
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={changePw}
              className="bg-[#F97316] hover:bg-[#EA580C] rounded-sm"
              data-testid="pw-confirm"
            >
              Speichern
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={permForm.open}
        onOpenChange={(o) => !o && setPermForm({ open: false, user: null, perms: {} })}
      >
        <AlertDialogContent className="rounded-sm max-w-2xl" data-testid="perms-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Berechtigungen — {permForm.user?.name}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Lege je Modul fest, ob der Benutzer keinen Zugriff hat, nur lesen oder schreiben darf.
              Admin-Module (Benutzer & Einstellungen) sind nur für Admin-Rolle verfügbar.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="max-h-[55vh] overflow-y-auto -mx-2 px-2">
            <div className="grid gap-2">
              {modules.map((mod) => {
                const isAdminOnly = mod.admin_only;
                const current = permForm.perms[mod.key] || "none";
                return (
                  <div
                    key={mod.key}
                    className={`flex items-center justify-between gap-3 p-3 border rounded-sm ${
                      isAdminOnly ? "bg-zinc-50 border-zinc-200" : "bg-white border-zinc-200"
                    }`}
                    data-testid={`perm-row-${mod.key}`}
                  >
                    <div>
                      <div className="font-medium text-sm">{mod.label}</div>
                      {isAdminOnly && (
                        <div className="mono text-[10px] tracking-wider text-zinc-500 mt-0.5">
                          NUR ADMIN-ROLLE
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {PERM_LEVELS.map((lvl) => (
                        <button
                          key={lvl.value}
                          type="button"
                          disabled={isAdminOnly}
                          onClick={() =>
                            setPermForm((s) => ({
                              ...s,
                              perms: { ...s.perms, [mod.key]: lvl.value },
                            }))
                          }
                          data-testid={`perm-${mod.key}-${lvl.value}`}
                          className={`px-3 py-1.5 text-xs font-medium border rounded-sm transition-all ${
                            current === lvl.value
                              ? lvl.cls + " border-current"
                              : "bg-white text-zinc-500 border-zinc-300 hover:bg-zinc-50"
                          } ${isAdminOnly ? "opacity-40 cursor-not-allowed" : ""}`}
                        >
                          {lvl.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={savePerms}
              data-testid="perms-save"
              className="bg-[#F97316] hover:bg-[#EA580C] rounded-sm"
            >
              Speichern
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
